import { DocCard, type DocCardItem, type LinkComponent } from './DocCard';

/**
 * A headed grid of destination cards — the one component behind the Docs home's
 * "Standards" (what your AI reads) and "Checks" (Validate gates) blocks, and
 * reusable anywhere a labelled set of destinations is shown. It owns the shared
 * shape: an eyebrow heading with an optional right-aligned hint, then a
 * two-column card grid. Each card's target and look come from its `DocCardItem`
 * (in-SPA `to`, external `href`, or disabled); routing stays with the caller
 * via the injected `link` component, so this file imports no router.
 */
export interface DocCardSectionProps {
  heading: string;
  /** Right-aligned eyebrow hint, e.g. "Served to every assistant over MCP". */
  hint?: string;
  items: DocCardItem[];
  /** In-SPA link renderer (react-router `Link`); required if any item uses `to`. */
  link?: LinkComponent;
}

export function DocCardSection({ heading, hint, items, link }: DocCardSectionProps) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {heading}
        </h2>
        {hint && <span className="text-xs text-slate-400 dark:text-slate-500">{hint}</span>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((item) => (
          <DocCard key={item.key} item={item} link={link} />
        ))}
      </div>
    </section>
  );
}
